import { useState, useEffect, useMemo, useCallback } from 'react';
import { useReadContract, useWatchContractEvent } from 'wagmi';
import { formatEther } from 'viem';
import { MEZO_CONTRACTS, INVOICE_CONTRACT_ABI, BORROW_MANAGER_ABI } from '@/lib/mezo';
import { firebaseVisitTracker as visitTracker } from '@/services/visit-tracker-firebase';
import { useExplorerData } from '@/hooks/useExplorerData';
import { clearExplorerCache } from '@/services/explorer-api';

export interface NetworkStats {
  totalUsers: number;
  totalInvoices: number;
  paidInvoices: number;
  pendingInvoices: number;
  cancelledInvoices: number;
  totalVolumeBTC: number;
  totalVolumeUSD: number; // Will be calculated with BTC price
  totalTVL: number; // Total collateral in vaults
  totalBorrowed: number; // Total MUSD borrowed
  activeVaults: number; // Estimated based on data
  paymentSuccessRate: number; // Percentage
  lastUpdated: number;
  connectedWallets: string[]; // Array of unique wallet addresses
  websiteVisits: number; // Total website visits
}

export interface NetworkActivity {
  invoiceId: string;
  type: 'created' | 'paid' | 'cancelled';
  timestamp: number;
  amount?: number;
  user?: string; // Masked for privacy
}

// Invoice struct from contract (matches Solidity)
interface ContractInvoice {
  id: bigint;
  creator: string;
  recipient: string;
  amount: bigint;
  description: string;
  bitcoinAddress: string;
  clientName: string;
  clientCode: string;
  paid: boolean;
  cancelled: boolean;
  createdAt: bigint;
  paidAt: bigint;
  expiresAt: bigint;
  payToAddress: string;
  paymentTxHash: string;
  observedInboundAmount: string;
  currency: string;
  balanceAtCreation: string;
}

export function useNetworkStats() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [bitcoinPrice, setBitcoinPrice] = useState<number>(65000); // Default fallback
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [websiteVisits, setWebsiteVisits] = useState<number>(0);
  const [websiteVisitsError, setWebsiteVisitsError] = useState<string | null>(null);
  const [isGlobalTracking, setIsGlobalTracking] = useState<boolean>(true); // Track if using Supabase (global) or localStorage (per-browser)

  const { stats: explorerStats, fromExplorer, isLoading: isExplorerLoading, refresh: refreshExplorer } = useExplorerData();

  // Fetch Bitcoin price (optional, for USD display)
  useEffect(() => {
    const fetchBitcoinPrice = async () => {
      try {
        const { fetchBitcoinPrice: fetchPrice } = await import('@/lib/bitcoin-price');
        const price = await fetchPrice();
        if (price && price > 0) {
          setBitcoinPrice(price);
        }
      } catch (err) {
        console.warn('Failed to fetch Bitcoin price, using default:', err);
        // Keep default value, don't update
      }
    };
    
    // Only fetch if in browser environment
    if (typeof window !== 'undefined') {
      fetchBitcoinPrice();
      const interval = setInterval(fetchBitcoinPrice, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, []);

  // Track website visits (Firebase real-time via visits_updated; poll only when localStorage fallback)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateVisits = async () => {
      try {
        const visits = await visitTracker.getTotalVisitsAsync();
        const isGlobal = visitTracker.isUsingGlobalTracking();
        setWebsiteVisits(visits);
        setIsGlobalTracking(isGlobal);

        if (!isGlobal) {
          setWebsiteVisitsError('Using local storage - count is per-browser, not global. Configure Firebase for global tracking.');
        } else {
          setWebsiteVisitsError(null);
        }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        setWebsiteVisitsError(msg);
        setIsGlobalTracking(false);
        try {
          setWebsiteVisits(visitTracker.getTotalVisits());
        } catch {
          setWebsiteVisits(0);
        }
      }
    };

    updateVisits();
    const handleVisitUpdate = () => updateVisits();
    window.addEventListener('visits_updated', handleVisitUpdate);

    // When Firebase is used, onValue drives real-time updates; skip polling. Otherwise poll every 5s.
    const interval = isGlobalTracking ? undefined : setInterval(updateVisits, 5000);

    return () => {
      window.removeEventListener('visits_updated', handleVisitUpdate);
      if (interval) clearInterval(interval);
    };
  }, [isGlobalTracking]);

  // Read total invoice count (faster, simpler call)
  const { 
    data: invoiceCount, 
    refetch: refetchInvoiceCount,
    isLoading: isInvoiceCountLoading,
    error: invoiceCountError
  } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'invoiceCount',
    chainId: 31611, // Explicitly specify Mezo testnet chain ID
    query: {
      enabled: true,
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: 5, // Increased retries for reliability
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
      staleTime: 10000,
      gcTime: 300000, // Keep in cache for 5 minutes
    },
  });

  // Read all invoices (this might be expensive for large datasets, but necessary)
  const { 
    data: allInvoicesData, 
    refetch: refetchAllInvoices,
    isLoading: isAllInvoicesLoading,
    error: allInvoicesError
  } = useReadContract({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    functionName: 'getAllInvoices',
    chainId: 31611, // Explicitly specify Mezo testnet chain ID
    query: {
      enabled: true, // Always fetch, no wallet needed
      refetchInterval: 30000, // Refetch every 30 seconds
      retry: 5, // Increased retries for reliability
      retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 15000), // Exponential backoff with longer delays for large queries
      staleTime: 10000,
      gcTime: 300000, // Keep in cache for 5 minutes
    },
  });

  // Handle errors: only block when RPC failed AND we have no Explorer fallback
  useEffect(() => {
    if (invoiceCountError) {
      console.error('❌ Invoice count query error:', {
        message: invoiceCountError.message,
        cause: invoiceCountError.cause,
        stack: invoiceCountError.stack,
        contract: MEZO_CONTRACTS.INVOICE_CONTRACT,
      });
    }
    if (allInvoicesError) {
      console.error('❌ All invoices query error:', {
        message: allInvoicesError.message,
        cause: allInvoicesError.cause,
        stack: allInvoicesError.stack,
        contract: MEZO_CONTRACTS.INVOICE_CONTRACT,
      });
    }
    const rpcFailed = !!(invoiceCountError || allInvoicesError);
    const hasExplorerFallback = !!(fromExplorer && explorerStats);

    if (rpcFailed && !hasExplorerFallback) {
      const rawMsg = invoiceCountError?.message || allInvoicesError?.message || 'Failed to fetch network stats';
      console.error('🚨 Network stats error:', rawMsg);
      console.error('   Contract address:', MEZO_CONTRACTS.INVOICE_CONTRACT);
      console.error('   RPC URL: https://rpc.test.mezo.org');
      setError('Could not load statistics from the blockchain. Check your network and try again.');
    } else {
      setError(null);
    }
  }, [invoiceCountError, allInvoicesError, fromExplorer, explorerStats]);

  // Read total collateral from vault
  const { data: totalCollateralData, refetch: refetchTVL, error: tvlError } = useReadContract({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    functionName: 'totalCollateral',
    chainId: 31611, // Explicitly specify Mezo testnet chain ID
    query: {
      enabled: true,
      refetchInterval: 30000,
      retry: 5,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  });

  // Read total borrowed from vault
  const { data: totalBorrowedData, refetch: refetchBorrowed, error: borrowedError } = useReadContract({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    functionName: 'totalBorrowed',
    chainId: 31611, // Explicitly specify Mezo testnet chain ID
    query: {
      enabled: true,
      refetchInterval: 30000,
      retry: 5,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  });

  // Log vault query errors
  useEffect(() => {
    if (tvlError) {
      console.error('❌ TVL query error:', tvlError.message, 'Contract:', MEZO_CONTRACTS.MEZO_VAULT);
    }
    if (borrowedError) {
      console.error('❌ Borrowed amount query error:', borrowedError.message, 'Contract:', MEZO_CONTRACTS.MEZO_VAULT);
    }
  }, [tvlError, borrowedError]);

  // Watch contract events for real-time updates
  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCreated',
    onLogs: () => {
      console.log('📊 Network Stats: InvoiceCreated event detected');
      setRefreshTrigger(v => v + 1);
      setTimeout(() => {
        refetchInvoiceCount();
        refetchAllInvoices();
      }, 2000); // Delay to ensure contract state is updated
    },
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoicePaid',
    onLogs: () => {
      console.log('📊 Network Stats: InvoicePaid event detected');
      setRefreshTrigger(v => v + 1);
      setTimeout(() => {
        refetchInvoiceCount();
        refetchAllInvoices();
      }, 2000);
    },
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.INVOICE_CONTRACT as `0x${string}`,
    abi: INVOICE_CONTRACT_ABI,
    eventName: 'InvoiceCancelled',
    onLogs: () => {
      console.log('📊 Network Stats: InvoiceCancelled event detected');
      setRefreshTrigger(v => v + 1);
      setTimeout(() => {
        refetchInvoiceCount();
        refetchAllInvoices();
      }, 2000);
    },
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'CollateralDeposited',
    onLogs: () => {
      console.log('📊 Network Stats: CollateralDeposited event detected');
      setRefreshTrigger(v => v + 1);
      setTimeout(() => {
        refetchTVL();
      }, 2000);
    },
  });

  useWatchContractEvent({
    address: MEZO_CONTRACTS.MEZO_VAULT as `0x${string}`,
    abi: BORROW_MANAGER_ABI,
    eventName: 'MUSDBorrowed',
    onLogs: () => {
      console.log('📊 Network Stats: MUSDBorrowed event detected');
      setRefreshTrigger(v => v + 1);
      setTimeout(() => {
        refetchBorrowed();
      }, 2000);
    },
  });

  // Track loading state (Explorer first, then RPC fallback)
  useEffect(() => {
    const loadingFromExplorer = fromExplorer ? isExplorerLoading : false;
    const loadingFromRpc = !fromExplorer &&
      (isInvoiceCountLoading || isAllInvoicesLoading) &&
      invoiceCount === undefined &&
      allInvoicesData === undefined;
    setIsLoading(loadingFromExplorer || loadingFromRpc);
  }, [fromExplorer, isExplorerLoading, isInvoiceCountLoading, isAllInvoicesLoading, invoiceCount, allInvoicesData]);

  // Log query status for debugging - CRITICAL for diagnosing data fetching issues
  useEffect(() => {
    const status = {
      invoiceCount: invoiceCount !== undefined ? Number(invoiceCount) : null,
      invoiceCountType: typeof invoiceCount,
      allInvoicesData: allInvoicesData !== undefined 
        ? (Array.isArray(allInvoicesData) ? `${allInvoicesData.length} invoices` : `non-array: ${typeof allInvoicesData}`)
        : null,
      invoiceCountError: invoiceCountError?.message || null,
      allInvoicesError: allInvoicesError?.message || null,
      contractAddress: MEZO_CONTRACTS.INVOICE_CONTRACT,
      isLoading: isInvoiceCountLoading || isAllInvoicesLoading,
      chainId: 31611,
      rpcUrl: 'https://rpc.test.mezo.org',
    };
    
    console.log('📊 Network Stats Query Status:', status);
    
    // Critical warnings
    if (invoiceCountError || allInvoicesError) {
      console.error('🚨 CRITICAL: Contract query errors detected!');
      console.error('   This means network stats will show zeros.');
      console.error('   Check RPC connection and contract addresses above.');
    } else if (invoiceCount !== undefined && allInvoicesData !== undefined) {
      console.log('✅ Contract queries successful - data should display correctly');
    }
  }, [invoiceCount, allInvoicesData, invoiceCountError, allInvoicesError, isInvoiceCountLoading, isAllInvoicesLoading]);

  // Calculate network stats from contract data (Explorer when complete, else RPC for all-time)
  const stats = useMemo<NetworkStats | null>(() => {
    const count = invoiceCount ? Number(invoiceCount) : 0;
    const totalTVL = totalCollateralData ? Number(formatEther(totalCollateralData as bigint)) : 0;
    const totalBorrowed = totalBorrowedData ? Number(formatEther(totalBorrowedData as bigint)) : 0;
    const activeVaults = totalCollateralData && Number(totalCollateralData) > 0n ? 1 : 0;

    // ALWAYS prioritize Explorer data - it's the public source of truth from blockchain
    // Explorer has all-time transaction data that everyone can verify
    // This ensures all users see the same public data
    const explorerComplete = fromExplorer && explorerStats;

    if (explorerComplete) {
      console.log('[useNetworkStats] ✅ Using PUBLIC Explorer data (same for everyone):', {
        totalInvoices: explorerStats!.totalInvoices,
        paidCount: explorerStats!.paidCount,
        pendingCount: explorerStats!.pendingCount,
        cancelledCount: explorerStats!.cancelledCount,
        uniqueCreators: explorerStats!.uniqueCreators.length,
        source: 'Explorer API (public blockchain data)',
      });
      const totalVolumeBTC = Number(formatEther(explorerStats!.totalVolumeWei));
      const totalVolumeUSD = totalVolumeBTC * bitcoinPrice;
      const totalNonCancelled = explorerStats!.paidCount + explorerStats!.pendingCount;
      const paymentSuccessRate = totalNonCancelled > 0
        ? Math.round((explorerStats!.paidCount / totalNonCancelled) * 100)
        : 0;
      return {
        totalUsers: explorerStats!.uniqueCreators.length,
        totalInvoices: explorerStats!.totalInvoices,
        paidInvoices: explorerStats!.paidCount,
        pendingInvoices: explorerStats!.pendingCount,
        cancelledInvoices: explorerStats!.cancelledCount,
        totalVolumeBTC,
        totalVolumeUSD,
        totalTVL,
        totalBorrowed,
        activeVaults,
        paymentSuccessRate,
        lastUpdated: Date.now(),
        connectedWallets: explorerStats!.uniqueCreators,
        websiteVisits,
      };
    }

    // RPC fallback: only when Explorer is unavailable
    // Note: RPC data is also public but Explorer is preferred for consistency
    console.log('[useNetworkStats] ⚠️ Explorer data not available, using RPC fallback');
    if (!allInvoicesData) {
      return {
        totalUsers: 0,
        totalInvoices: count,
        paidInvoices: 0,
        pendingInvoices: 0,
        cancelledInvoices: 0,
        totalVolumeBTC: 0,
        totalVolumeUSD: 0,
        totalTVL,
        totalBorrowed,
        activeVaults,
        paymentSuccessRate: 0,
        lastUpdated: Date.now(),
        connectedWallets: [],
        websiteVisits,
      };
    }

    try {
      setError(null);

      const invoices = allInvoicesData as unknown as ContractInvoice[];
      
      if (!Array.isArray(invoices)) {
        console.error('❌ allInvoicesData is not an array:', typeof allInvoicesData, allInvoicesData);
        setError('Invalid invoice data format received from contract');
        return null;
      }

      console.log('📋 Processing invoices:', invoices.length, 'total invoices from blockchain');
      
      if (invoices.length === 0) {
        console.log('ℹ️ No invoices found on blockchain yet - showing empty stats');
        // Still return stats with zeros, but log the invoice count from contract
        const contractInvoiceCount = count ? Number(count) : 0;
        console.log('   Contract invoiceCount:', contractInvoiceCount);
        if (contractInvoiceCount > 0 && invoices.length === 0) {
          console.warn('⚠️ Contract says', contractInvoiceCount, 'invoices exist, but getAllInvoices returned empty array');
          console.warn('   This might indicate a contract query issue or data format mismatch');
        }
        return {
          totalUsers: 0,
          totalInvoices: contractInvoiceCount, // Use contract count even if array is empty
          paidInvoices: 0,
          pendingInvoices: 0,
          cancelledInvoices: 0,
          totalVolumeBTC: 0,
          totalVolumeUSD: 0,
          totalTVL: totalCollateralData ? Number(formatEther(totalCollateralData as bigint)) : 0,
          totalBorrowed: totalBorrowedData ? Number(formatEther(totalBorrowedData as bigint)) : 0,
          activeVaults: totalCollateralData && Number(totalCollateralData) > 0n ? 1 : 0,
          paymentSuccessRate: 0,
          lastUpdated: Date.now(),
          connectedWallets: [], // Empty array when no data
          websiteVisits: websiteVisits,
        };
      }

      // Extract unique creator addresses (real wallet addresses)
      const uniqueUsers = new Set<string>();
      const walletAddresses: string[] = [];
      invoices.forEach(inv => {
        if (inv.creator && inv.creator !== '0x0000000000000000000000000000000000000000') {
          const addr = inv.creator.toLowerCase();
          if (!uniqueUsers.has(addr)) {
            uniqueUsers.add(addr);
            walletAddresses.push(inv.creator); // Keep original casing for display
          }
        }
      });
      
      // Sort wallets by most recent activity (wallets with more invoices first)
      walletAddresses.sort((a, b) => {
        const aCount = invoices.filter(inv => inv.creator.toLowerCase() === a.toLowerCase()).length;
        const bCount = invoices.filter(inv => inv.creator.toLowerCase() === b.toLowerCase()).length;
        return bCount - aCount;
      });

      // Calculate invoice statuses
      const now = Math.floor(Date.now() / 1000);
      let paidCount = 0;
      let pendingCount = 0;
      let cancelledCount = 0;
      let totalVolumeWei = 0n;

      invoices.forEach(inv => {
        if (inv.cancelled) {
          cancelledCount++;
        } else if (inv.paid) {
          paidCount++;
          // Use observed amount if available, otherwise use requested amount
          if (inv.observedInboundAmount && inv.observedInboundAmount !== '0' && inv.observedInboundAmount !== '') {
            try {
              totalVolumeWei += BigInt(inv.observedInboundAmount);
            } catch {
              totalVolumeWei += inv.amount;
            }
          } else {
            totalVolumeWei += inv.amount;
          }
        } else {
          // Check if expired
          const expiresAt = Number(inv.expiresAt);
          if (expiresAt > now) {
            pendingCount++;
          }
        }
      });

      // Convert wei to BTC (1 BTC = 1e18 wei)
      const totalVolumeBTC = Number(formatEther(totalVolumeWei));
      const totalVolumeUSD = totalVolumeBTC * bitcoinPrice;

      // Get TVL and borrowed amounts
      const totalTVL = totalCollateralData ? Number(formatEther(totalCollateralData as bigint)) : 0;
      const totalBorrowed = totalBorrowedData ? Number(formatEther(totalBorrowedData as bigint)) : 0;

      // Calculate payment success rate
      const totalNonCancelled = paidCount + pendingCount;
      const paymentSuccessRate = totalNonCancelled > 0 
        ? Math.round((paidCount / totalNonCancelled) * 100) 
        : 0;

      // Estimate active vaults (vaults with collateral)
      const activeVaults = totalTVL > 0 ? 1 : 0;

      // Use Explorer for connected wallets + totalUsers when available (all-time tx.from)
      const useExplorerWallets = fromExplorer && explorerStats && explorerStats.uniqueCreators.length > 0;
      const connectedWallets = useExplorerWallets ? explorerStats!.uniqueCreators : walletAddresses;
      const totalUsers = useExplorerWallets ? explorerStats!.uniqueCreators.length : uniqueUsers.size;

      return {
        totalUsers,
        totalInvoices: invoices.length,
        paidInvoices: paidCount,
        pendingInvoices: pendingCount,
        cancelledInvoices: cancelledCount,
        totalVolumeBTC,
        totalVolumeUSD,
        totalTVL,
        totalBorrowed,
        activeVaults,
        paymentSuccessRate,
        lastUpdated: Date.now(),
        connectedWallets,
        websiteVisits: websiteVisits,
      };
    } catch (err) {
      console.error('Error calculating network stats:', err);
      setError('Failed to calculate network stats');
      setIsLoading(false);
      return null;
    }
  }, [allInvoicesData, totalCollateralData, totalBorrowedData, bitcoinPrice, refreshTrigger, websiteVisits, fromExplorer, explorerStats]);

  // Generate recent activity feed (Explorer events when fromExplorer, else RPC invoices)
  const recentActivity = useMemo<NetworkActivity[]>(() => {
    if (fromExplorer && explorerStats?.events?.length) {
      const activities: NetworkActivity[] = explorerStats.events.map((ev) => ({
        invoiceId: String(ev.id),
        type: ev.type,
        timestamp: ev.timestamp,
        amount: ev.amount ? Number(formatEther(BigInt(ev.amount))) : undefined,
        user: ev.creator ? `${ev.creator.slice(0, 6)}...${ev.creator.slice(-4)}` : undefined,
      }));
      return activities.sort((a, b) => b.timestamp - a.timestamp).slice(0, 20);
    }

    if (!allInvoicesData) return [];

    try {
      const invoices = allInvoicesData as unknown as ContractInvoice[];
      const activities: NetworkActivity[] = [];

      invoices.forEach(inv => {
        activities.push({
          invoiceId: inv.id.toString(),
          type: 'created',
          timestamp: Number(inv.createdAt),
          amount: Number(formatEther(inv.amount)),
          user: inv.creator ? `${inv.creator.slice(0, 6)}...${inv.creator.slice(-4)}` : undefined,
        });

        if (inv.paid && Number(inv.paidAt) > 0) {
          const paidAmount = inv.observedInboundAmount && inv.observedInboundAmount !== '0'
            ? Number(formatEther(BigInt(inv.observedInboundAmount)))
            : Number(formatEther(inv.amount));
          activities.push({
            invoiceId: inv.id.toString(),
            type: 'paid',
            timestamp: Number(inv.paidAt),
            amount: paidAmount,
            user: inv.creator ? `${inv.creator.slice(0, 6)}...${inv.creator.slice(-4)}` : undefined,
          });
        }

        if (inv.cancelled) {
          activities.push({
            invoiceId: inv.id.toString(),
            type: 'cancelled',
            timestamp: Number(inv.createdAt),
            user: inv.creator ? `${inv.creator.slice(0, 6)}...${inv.creator.slice(-4)}` : undefined,
          });
        }
      });

      return activities.sort((a, b) => b.timestamp - a.timestamp).slice(0, 20);
    } catch (err) {
      console.error('Error generating activity feed:', err);
      return [];
    }
  }, [allInvoicesData, refreshTrigger, fromExplorer, explorerStats]);

  const refresh = useCallback(() => {
    // Clear Explorer cache to force fresh data fetch - ensure public data consistency
    clearExplorerCache();
    console.log('[useNetworkStats] Refreshing all data sources for public consistency');
    setRefreshTrigger(v => v + 1);
    refreshExplorer(); // This will force fresh Explorer fetch
    refetchInvoiceCount();
    refetchAllInvoices();
    refetchTVL();
    refetchBorrowed();
  }, [refreshExplorer, refetchInvoiceCount, refetchAllInvoices, refetchTVL, refetchBorrowed]);

  // Always return stats (never null), even if empty
  const finalStats: NetworkStats = stats || {
    totalUsers: 0,
    totalInvoices: invoiceCount ? Number(invoiceCount) : 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    cancelledInvoices: 0,
    totalVolumeBTC: 0,
    totalVolumeUSD: 0,
    totalTVL: totalCollateralData ? Number(formatEther(totalCollateralData as bigint)) : 0,
    totalBorrowed: totalBorrowedData ? Number(formatEther(totalBorrowedData as bigint)) : 0,
    activeVaults: 0,
    paymentSuccessRate: 0,
    lastUpdated: Date.now(),
    connectedWallets: [], // Empty array when no data
    websiteVisits: websiteVisits,
  };

  const rpcWarning =
    (!!invoiceCountError || !!allInvoicesError) && !!(fromExplorer && explorerStats);
  const explorerOk = !!(fromExplorer && explorerStats);
  const rpcOk = !rpcWarning;

  return {
    stats: finalStats,
    recentActivity,
    isLoading,
    error,
    rpcWarning,
    explorerOk,
    rpcOk,
    refresh,
    bitcoinPrice,
    websiteVisitsError,
    isGlobalTracking,
  };
}
